import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MILL_SECONDS } from 'common/constant/general';
import { ALIGN, POSITION } from 'components/analyze/analyze-common';
import { MB_RESPONSE, MB_TYPE, MessageBoxComponent, MessageBoxOptions } from 'components/message-box/message-box.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { DefinitionService } from 'services/definition/definition.service';
import { ISourceName } from 'services/definition/interface';
import { SpinnerTypes } from 'services/loading/spinner/spinner-type';
import { Logger } from 'src/app/common/utility/logger/logger';

// Loading Source List const
const MAX_TIMEOUT = 60;

// LineSettings contained within TelemetryNode of sourceNodes tree
export interface DTCSettings {
  sourceId: string | undefined;
  title: {
    showTitle: boolean
    text: string
    position: POSITION
    align: ALIGN
    font: {
      weight: string
      size: number
    }
  }
  columnSettings: Array<DTC_Column_Settings>,
}

export interface DTC_Column_Settings {
  columnId: string,
  display: boolean
}

export const enum COLUMN_ID {
  deviceDisplay = "deviceDisplay",
  codeDisplay = "codeDisplay",
  status = "status",
  accommodationLevel = "accommodationLevel",
  occurrenceCount = "occurrenceCount",
  // totalActiveTime = "totalActiveTime",
  eventTimestamp = "eventTimestamp"
}

const DEFAULT_DTC_SETTINGS: Array<DTC_Column_Settings> = [
  { columnId: COLUMN_ID.deviceDisplay, display: true },
  { columnId: COLUMN_ID.codeDisplay, display: true },
  { columnId: COLUMN_ID.status, display: true },
  { columnId: COLUMN_ID.accommodationLevel, display: true },
  { columnId: COLUMN_ID.occurrenceCount, display: false },
  // { columnId: COLUMN_ID.totalActiveTime, display: false },
  { columnId: COLUMN_ID.eventTimestamp, display: false }
]

const DTC_DEFAULT_SETTINGS = {
  sourceId: undefined,
  title: {
    showTitle: false,
    text: "",
    position: POSITION.Top,
    align: ALIGN.Start,
    font: {
      weight: 'bolder',
      size: 14
    }
  },
  columnSettings: DEFAULT_DTC_SETTINGS,
}

export interface DTCDisplaySettings {
  columnId: COLUMN_ID,
  columnDisplayName: string,
  frmDisplayName: string,
  frmCtrlName: string,
  display: boolean
}

const DefaultColumnDisplaySettings: Array<DTCDisplaySettings> = [
  { columnId: COLUMN_ID.deviceDisplay, columnDisplayName: "column-device", frmDisplayName: "display-device", frmCtrlName: "displayDeviceFrmCtl", display: true },
  { columnId: COLUMN_ID.codeDisplay, columnDisplayName: "column-code", frmDisplayName: "display-code", frmCtrlName: "displayCodeFrmCtl", display: true },
  { columnId: COLUMN_ID.status, columnDisplayName: "column-status", frmDisplayName: "display-status", frmCtrlName: "displayStatusFrmCtl", display: true },
  { columnId: COLUMN_ID.accommodationLevel, columnDisplayName: "column-level", frmDisplayName: "display-level", frmCtrlName: "displayLevelFrmCtl", display: true },
  { columnId: COLUMN_ID.occurrenceCount, columnDisplayName: "column-count", frmDisplayName: "display-count", frmCtrlName: "displayCountFrmCtl", display: false },
  // { columnId: COLUMN_ID.totalActiveTime, columnDisplayName: "column-active-time", frmDisplayName: "display-active-time", frmCtrlName: "displayActiveTimeFrmCtl", display: false },
  { columnId: COLUMN_ID.eventTimestamp, columnDisplayName: "column-last-timestamp", frmDisplayName: "display-last-timestamp", frmCtrlName: "displayLastTimestampFrmCtl", display: false }
]


@Component({
  selector: 'dtc-widget-settings',
  templateUrl: './dtc-widget-settings.component.html',
  styleUrls: ['./dtc-widget-settings.component.scss']
})
export class DTCWidgetSettingsComponent implements OnInit {
  public sourcesList: Array<ISourceName> = [];
  public columnDisplaySettings: Array<DTCDisplaySettings> = [];
  private form: any = [];

  // Form Controls
  public userForm!: FormGroup;
  public titleShowFrmCtrl = new FormControl();
  public titleTextFrmCtrl = new FormControl();
  public titleAlignFrmCtrl = new FormControl();
  public titleSizeFrmCtrl = new FormControl();

  public getSourceId(source: ISourceName): string {
    return source.sourceId
  } // getSourceId()

  constructor(
    private dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<DTCWidgetSettingsComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { ndx: any, settings: DTCSettings }
  ) { }

  private loadingId = 0;
  private loadingTimeout = MAX_TIMEOUT;
  public ngOnInit() {

    // If Sources List is not ready, then wait
    if (!DefinitionService.SourcesListReady) {
      this.spinner.show(this.spinnerName);

      this.loadingId = window.setInterval(() => {
        // Did we timeout?
        if (this.loadingTimeout && !--this.loadingTimeout) {
          Logger.error("Source Selection List Timed Out")
          // Tell the user we timed out
          const msgBoxSettings: MessageBoxOptions = {
            type: MB_TYPE.YES_NO,
            title: undefined,
            message: "widgets.common.source-list-timeout"
          }
          const msgBox = this.dialog.open(MessageBoxComponent, {
            data: msgBoxSettings
          });
          msgBox.afterClosed().subscribe((res) => {
            if (res.event === MB_RESPONSE.YES) {
              this.loadingTimeout = MAX_TIMEOUT;
            } else {
              this.spinner.hide(this.spinnerName);
              window.clearInterval(this.loadingId)
              this.loadingId = 0;
              // Close dialog
              this.dialogRef.close({
                event: 'cancel',
                data: undefined
              });
            }
          }); // afterClosed subscribe
        } // If loadingTimeout

        // See if the Source List is Ready yet
        if (DefinitionService.SourcesListReady) {
          this.spinner.hide(this.spinnerName)
          window.clearInterval(this.loadingId);
          this.loadingId = 0;
          this.sourcesList = DefinitionService.getSourceNames();
        }
      }, MILL_SECONDS)
    } else { // Sit list is Ready
      // Get my sourceNodes
      this.sourcesList = DefinitionService.getSourceNames();
    }

    // If data settings have not been initialized
    if (this.data.settings === undefined) {
      this.data.settings = DTC_DEFAULT_SETTINGS;
    }

    // Init the table settings
    this.columnDisplaySettings = DTCWidgetSettingsComponent.dtcGetDisplaySettings(this.data.settings.columnSettings)

    // Setup the userForm for the dialog
    this.form = {
      sourceIdSelectionFrmCtl: this.data.settings.sourceId,
      titleShowFrmCtrl: this.data.settings.title.showTitle,
      titleTextFrmCtrl: this.data.settings.title.text,
      titleAlignFrmCtrl: this.data.settings.title.align,
      titleSizeFrmCtrl: this.data.settings.title.font.size
    }

    this.columnDisplaySettings.forEach(col => {
      this.form[col.frmCtrlName] = col.display
    })

    this.userForm = this.formBuilder.group(this.form);

  } // ngOnInit()

  public get spinnerType(): string {
    return SpinnerTypes.BallClipRotate;
  }

  public get spinnerName(): string {
    return "sourceList";
  }

  public get isSourcesListReady(): boolean {
    return DefinitionService.SourcesListReady;
  }

  public static dtcGetDisplaySettings(dtcSettings: Array<DTC_Column_Settings>): Array<DTCDisplaySettings> {
    let dtcDisplaySettings: Array<DTCDisplaySettings> = []

    dtcSettings.forEach(col => {
      const found = DefaultColumnDisplaySettings.find(def => def.columnId == col.columnId ? def : undefined)
      if (found) {
        found.display = col.display
        dtcDisplaySettings.push(found)
      }
    })

    return dtcDisplaySettings
  } // dtcGetDisplaySettings()

  public static dtcGetSettings(dtcDisplay: Array<DTCDisplaySettings>): Array<DTC_Column_Settings> {
    let dtcSettings: Array<DTC_Column_Settings> = []

    dtcDisplay.forEach(col => {
      const setting: DTC_Column_Settings = {
        columnId: col.columnId,
        display: col.display
      }
      dtcSettings.push(setting)
    })

    return dtcSettings
  } // dtcGetSettings()

  public SettingsLabels(str: string): string {
    return 'widgets.dtc-widget.' + str;
  } // localizedModalSettings()


  // Used by Dashboard when it creates a Line Widget
  public static getDefault(): DTCSettings {
    return DTC_DEFAULT_SETTINGS;
  } // getDefault()

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.columnDisplaySettings, event.previousIndex, event.currentIndex);
  } // drop()

  public isApplyDisabled(): boolean {
    return this.userForm.get('sourceIdSelectionFrmCtl')?.getRawValue() == null;
  }

  // Apply settings selected, update settings
  public apply() {
    // Save the changes
    this.data.settings.sourceId = this.userForm.get('sourceIdSelectionFrmCtl')?.getRawValue()

    // Get the Form changes
    this.columnDisplaySettings.forEach(col => {
      col.display = this.userForm.get(col.frmCtrlName)?.getRawValue()
    })

    this.data.settings.columnSettings = DTCWidgetSettingsComponent.dtcGetSettings(this.columnDisplaySettings)

    this.data.settings.title.showTitle = this.userForm.get('titleShowFrmCtrl')?.getRawValue()
    this.data.settings.title.text = this.userForm.get('titleTextFrmCtrl')?.getRawValue()
    this.data.settings.title.align = this.userForm.get('titleAlignFrmCtrl')?.getRawValue()
    this.data.settings.title.font.size = this.userForm.get('titleSizeFrmCtrl')?.getRawValue()

    // Apply selected, return selected data
    this.dialogRef.close({
      event: 'apply',
      data: this.data
    });
  } // apply()

  // Cancel selected, discard settings
  public cancel() {
    // If we are loading, then cleanup
    if (this.loadingId) {
      this.spinner.hide(this.spinnerName);
      window.clearInterval(this.loadingId)
      this.loadingId = 0;
    }
    // Issue Close
    this.dialogRef.close({
      event: 'cancel',
      data: undefined
    });
  } // cancel()

  public widgetCommonLabels(str: string): string {
    return 'widgets.common.' + str;
  } // tableSettingsLabels()

  public get getALIGN(): any {
    return ALIGN;
  }

} // class DTCWidgetSettingsComponent
